import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PomodoroPhase } from '@/hooks/usePomodoro';
import { usePomodoroContext } from '@/contexts/PomodoroContext';
import { useProjects } from '@/hooks/useProject';

const PHASE_COLORS: Record<PomodoroPhase, { ring: string; bg: string; tab: string; tabActive: string }> = {
  work: {
    ring: 'stroke-red-500',
    bg: 'text-red-500',
    tab: 'text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30',
    tabActive: 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400',
  },
  shortBreak: {
    ring: 'stroke-green-500',
    bg: 'text-green-500',
    tab: 'text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30',
    tabActive: 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400',
  },
  longBreak: {
    ring: 'stroke-blue-500',
    bg: 'text-blue-500',
    tab: 'text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30',
    tabActive: 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400',
  },
};

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatFocusTime(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const PomodoroPage: React.FC = () => {
  const { t } = useTranslation('pomodoro');
  const [showSettings, setShowSettings] = useState(false);

  const {
    phase,
    running,
    remainingSeconds,
    totalDuration,
    sessionsCompleted,
    totalFocusSeconds,
    settings,
    setSettings,
    selectedProject,
    setSelectedProject,
    start,
    pause,
    reset,
    skip,
    setPhase,
    resetDefaults,
    longBreakInterval,
  } = usePomodoroContext();

  const { data: projectsData } = useProjects();
  const activeProjects = useMemo(() =>
    (projectsData?.projects || []).filter(p => p.status === 'active' || p.status === 'planning'),
    [projectsData]
  );

  const colors = PHASE_COLORS[phase];
  const progress = totalDuration > 0 ? (totalDuration - remainingSeconds) / totalDuration : 0;

  // SVG circle dimensions
  const size = 256; // viewBox size
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  const phases: PomodoroPhase[] = ['work', 'shortBreak', 'longBreak'];

  return (
    <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
      {/* Title */}
      <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white">
        {t('title')}
      </h1>

      {/* Project selector */}
      <div className="flex justify-center">
        <select
          value={selectedProject?.id || ''}
          onChange={(e) => {
            const proj = activeProjects.find(p => p.id === e.target.value);
            setSelectedProject(proj ? { id: proj.id, project_name: proj.project_name, project_code: proj.project_code } : null);
          }}
          className="w-full max-w-xs px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">{t('noProject')}</option>
          {activeProjects.map(p => (
            <option key={p.id} value={p.id}>{p.project_code} - {p.project_name}</option>
          ))}
        </select>
      </div>

      {/* Phase tabs */}
      <div className="flex justify-center gap-2">
        {phases.map((p) => {
          const c = PHASE_COLORS[p];
          const active = phase === p;
          return (
            <button
              key={p}
              onClick={() => setPhase(p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${active ? c.tabActive : c.tab}`}
            >
              {t(`phases.${p}`)}
            </button>
          );
        })}
      </div>

      {/* Timer ring */}
      <div className="flex justify-center">
        <div className="relative w-48 sm:w-64 lg:w-80">
          <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
            {/* Background circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={strokeWidth}
              className="text-gray-200 dark:text-gray-700"
            />
            {/* Progress circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              className={`${colors.ring} transition-[stroke-dashoffset] duration-300`}
            />
          </svg>
          {/* Countdown digits centered inside */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-4xl sm:text-5xl lg:text-6xl font-mono font-bold ${colors.bg}`}>
              {formatTime(remainingSeconds)}
            </span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex justify-center gap-3">
        {running ? (
          <button
            onClick={pause}
            className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            {t('controls.pause')}
          </button>
        ) : (
          <button
            onClick={start}
            className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
          >
            {t('controls.start')}
          </button>
        )}
        <button
          onClick={reset}
          className="px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          {t('controls.reset')}
        </button>
        <button
          onClick={skip}
          className="px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          {t('controls.skip')}
        </button>
      </div>

      {/* Stats */}
      <div className="flex justify-center gap-8 text-center">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('stats.sessions')}</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('stats.sessionCount', { completed: sessionsCompleted, total: longBreakInterval })}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('stats.todayFocus')}</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatFocusTime(totalFocusSeconds)}
          </div>
        </div>
      </div>

      {/* Settings toggle */}
      <div className="flex justify-center">
        <button
          onClick={() => setShowSettings((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          <svg className={`w-4 h-4 transition-transform ${showSettings ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          {t('settings.title')}
        </button>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-700 p-4 space-y-4">
          {/* Duration inputs */}
          {([
            ['workDuration', 'settings.workDuration'],
            ['shortBreakDuration', 'settings.shortBreakDuration'],
            ['longBreakDuration', 'settings.longBreakDuration'],
            ['longBreakInterval', 'settings.longBreakInterval'],
          ] as const).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between">
              <label className="text-sm text-gray-700 dark:text-gray-300">{t(label)}</label>
              <input
                type="number"
                min={1}
                max={key === 'longBreakInterval' ? 10 : 120}
                value={settings[key]}
                onChange={(e) => setSettings({ ...settings, [key]: Math.max(1, parseInt(e.target.value) || 1) })}
                className="w-16 px-2 py-1 text-center text-sm border rounded-md bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              />
            </div>
          ))}

          {/* Toggle switches */}
          {([
            ['autoStartBreaks', 'settings.autoStartBreaks'],
            ['autoStartWork', 'settings.autoStartWork'],
            ['soundEnabled', 'settings.soundEnabled'],
          ] as const).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between">
              <label className="text-sm text-gray-700 dark:text-gray-300">{t(label)}</label>
              <button
                onClick={() => setSettings({ ...settings, [key]: !settings[key] })}
                className={`relative w-10 h-5 rounded-full transition-colors ${settings[key] ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${settings[key] ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
            </div>
          ))}

          {/* Reset defaults */}
          <button
            onClick={resetDefaults}
            className="w-full py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          >
            {t('settings.resetDefaults')}
          </button>
        </div>
      )}
    </div>
  );
};

export default PomodoroPage;
